import { useThemeStore } from "@stores/theme.store";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Input, message } from "antd";
import { useMutation } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { NoticeType } from "antd/es/message/interface";
import { PopupSuccess } from "@components/general/popup";
import { linkPage } from "@utils/router";
import { LuShield } from "react-icons/lu";

export const SettingChangePasswordPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setHeader, setAppBar } = useThemeStore((state) => state);
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [inputDisabled, setInputDisabled] = useState<boolean>(true);
  const [oldPasswordError, setOldPasswordError] = useState<boolean>(false);
  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nis = state.nis ?? "";

  const { mutate: onUpdatePassword, isPending: isPendingUpdateBio } =
    useMutation({
      mutationFn: SchoolService.updatePassword,
      onSuccess: (response) => {
        setPopupSuccess(true);
      },
      onError: (error: any) => {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(error.message ?? "Internal Error");
      },
    });

  const { mutate: checkPassword, isPending: isPendingCheckPassword } =
    useMutation({
      mutationFn: SchoolService.checkPassword,
      onSuccess: (response) => {
        if (response.status === false) {
          setShowMessage(true);
          setMessageType("error");
          setShowMessageText(response.message);
          setInputDisabled(true);
          setOldPasswordError(true);
        } else {
          setInputDisabled(false);
          setOldPasswordError(false);
        }
      },
      onError: (error: any) => {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(error.message ?? "Internal Error");
      },
    });

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    setAppBar({ title: "Ubah Password", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      title: "Ubah Password",
      filter: false,
      image: false,
    });
  }, [setHeader, setAppBar]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-0">
        <Form
          className=""
          autoComplete="off"
          onFinish={(data) =>
            onUpdatePassword({
              nis: nis,
              password: data.password,
            }) as any
          }
        >
          <div className="tw-mb-4 tw-text-accent-primary card-body">
            <Form.Item
              label="Password Lama"
              name="oldPassword"
              className="tw-mb-4"
            >
              <Input
                suffix={<LuShield size={20} className="tw-text-text-primary" />}
                className="tw-border-background tw-shadow-none hover:tw-border-primary focus:tw-shadow-none tw-text-base"
                placeholder=""
                status={oldPasswordError ? "error" : undefined}
                onChange={(e) => {
                  const value = e.target.value;
                  setInputDisabled(true);
                  setOldPasswordError(false);
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  if (value) {
                    debounceRef.current = setTimeout(() => {
                      checkPassword({ nis: nis, oldPassword: value }) as any;
                    }, 600);
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              label="Password Baru"
              name="password"
              className="tw-mb-4"
              rules={[{ required: true, message: "Password baru tidak boleh kosong" }]}
            >
              <Input.Password
                className="tw-border-background tw-shadow-none hover:tw-border-primary focus:tw-shadow-none tw-text-base"
                placeholder="Password"
                // disabled={inputDisabled}
              />
            </Form.Item>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            loading={isPendingUpdateBio || isPendingCheckPassword}
            className="tw-p-2 tw-mt-4 tw-h-10 tw-w-full tw-text-base tw-rounded-xl bg-gradient tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
            disabled={inputDisabled}
          >
            Simpan Password
          </Button>
        </Form>
      </div>

      <PopupSuccess
        open={popupSuccess}
        title="Berhasil!"
        message="Password berhasil diubah!"
        textConfirm="Kembali"
        onConfirm={() => navigate(linkPage.HOME_PROFILE)}
      />
    </>
  );
};
